import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { HttpBaseClient } from '@/services/httpBaseClient'
import { useAuthStore } from '@/stores/auth'
import { fetchClientInstanceId } from '@/utils/clientInstance'

export interface SSOCheckResponse {
  success: boolean;
  canAuthenticateInternal: boolean;
  canAuthenticateExternal: boolean;
  externalIdPs: Array<{
      logo: string | null;
      name: string;
      description: string;
      url: string;
  }>;
  current_server_time: string;
}

export interface AcsLoginResponse {
  success: boolean
  authenticated?: boolean
  redirectUrl?: string
  current_server_time: string
}

/**
 * Composable for handling SSO/ACS (Single Sign-On/Assertion Consumer Service) login flow
 */
export function useSSO() {
  const router = useRouter()
  const authStore = useAuthStore()
  const apiBaseUrl = ref(import.meta.env.VITE_BOOMTOWN_API_HOST)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  // Create base HTTP client for auth-related API calls
  const baseHttpClient = new HttpBaseClient(apiBaseUrl.value)

  /**
   * Check if the user is authenticated via SSO
   * @param email - The email address of the user to check
   * @returns The SSO check response
   */
  const checkSSO = async (email: string): Promise<SSOCheckResponse> => {
    try {
        console.debug('🔍 Auth Store: Checking SSO status for email:', email)
        
        const formData = new URLSearchParams()
        formData.append('email', email)

        const data = await baseHttpClient.post<SSOCheckResponse>(
            'admin/v4/core/',
            formData,
            { sAction: 'getUserAuth' },
            {
                'Content-Type': 'application/x-www-form-urlencoded',
                'x-boomtown-client-instance-id': fetchClientInstanceId(),
                'x-boomtown-csrf-token': authStore.csrfToken,
                'x-boomtown-app': 'cxme',
            }
        )

        console.debug('✅ Auth Store: SSO check completed successfully')
        return data
    } catch (error) {
        console.error('❌ Auth Store: Failed to check SSO status:', error)
        throw new Error('Failed to check SSO status')
    }
  }

  /**
   * Submit ACS login request with preflight parameter
   * @param preflight - UUID preflight parameter
   */
  const submitAcsLogin = async (preflight: string, ptid: string): Promise<AcsLoginResponse> => {
    isLoading.value = true
    error.value = null

    try {
      const formData = new URLSearchParams()
      formData.append('preflight', preflight)

      const data = await baseHttpClient.post<AcsLoginResponse>(
        'admin/v4/sso/saml/' + ptid + '/acs',
        formData,
        {},
        {
          'Content-Type': 'application/x-www-form-urlencoded',
          'x-boomtown-client-instance-id': fetchClientInstanceId(),
          'x-boomtown-csrf-token': authStore.csrfToken,
          'x-boomtown-app': 'cxme',
        }
      )
      console.debug('✅ acs login: completed successfully', data)
      
      // Handle successful authentication - redirect using same logic as Login.vue
      if (data.success && data.authenticated) {
        authStore.isAuthenticated = true
        // Prevent router guard from calling initialize() again after SSO login
        authStore.isInitialized = true
        // Add a small delay to ensure cookies are properly set by the browser before refreshing
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // For SSO, we need to refresh CSRF token to get user data
        // Skip automatic partner/meta data loading to prevent CSRF token conflicts
        await authStore.refreshCsrfToken(true)
        
        // Clean up timeout redirect flag after successful SSO login
        sessionStorage.removeItem('redirectAfterTimeoutLogin')

        // Now manually trigger partner and meta data loading with the updated CSRF token
        try {
          const { usePartnerStore } = await import('@/stores/partner')
          const { useMetaStore } = await import('@/stores/meta')
          const { useUserStore } = await import('@/stores/user')
          
          const partnerStore = usePartnerStore()
          const metaStore = useMetaStore()
          const userStore = useUserStore()
          
          // Load partner data if we have a user object_id
          if (userStore.userData?.object_id) {
            try {
              await partnerStore.fetchCurrentPartner()
              console.log('🏢 SSO: Successfully fetched current partner data')
            } catch (partnerError) {
              console.error('🏢 SSO: Failed to fetch current partner data:', partnerError)
            }
          }
          
          // Load meta data
          try {
            await Promise.all([
              metaStore.loadMetaData(),
              metaStore.loadPartnerMetaData()
            ])
            console.log('📊 SSO: Successfully loaded meta data and partner meta data')
          } catch (metaError) {
            console.error('📊 SSO: Failed to load meta data:', metaError)
          }
        } catch (error) {
          console.error('❌ SSO: Failed to load additional data after CSRF refresh:', error)
        }

        if (authStore.redirectAfterLogin) {          
          // If redirect after login is a full URL, redirect to it directly
          if (authStore.redirectAfterLogin.startsWith('https')) {
            window.location.href = authStore.redirectAfterLogin
            authStore.redirectAfterLogin = ''
          } else {
            // Validate redirect path before navigating to prevent circular navigation
            const isValidRedirect = authStore.redirectAfterLogin !== '/login' && 
                                   !authStore.redirectAfterLogin.startsWith('/login?') &&
                                   authStore.redirectAfterLogin !== '/passwordreset' && 
                                   !authStore.redirectAfterLogin.startsWith('/passwordreset?')
            
            if (isValidRedirect) {
              try {
                await router.push(authStore.redirectAfterLogin)
                authStore.redirectAfterLogin = ''
              } catch (error) {
                // Log navigation failure as error for DataDog tracking
                console.error('❌ SSO: Redirect navigation failed:', {
                  redirectPath: authStore.redirectAfterLogin,
                  error: error instanceof Error ? error.message : 'Unknown error'
                })
                
                // Clear the failed redirect and fall back to inbox home
                authStore.redirectAfterLogin = ''
                await router.push('/inbox?view=home')
              }
            } else {
              // Invalid redirect path, clear it and go to inbox home
              console.log('🏠 useSSO: Invalid redirect path, going to inbox home instead')
              authStore.redirectAfterLogin = ''
              await router.push('/inbox?view=home')
            }
          }
        } else {
          // Default redirect to inbox home view
          console.log('🏠 useSSO: No saved redirect, going to inbox home')
          await router.push('/inbox?view=home')
        }
      }
      
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      console.error('❌ AcsLogin: ACS login error:', errorMessage)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    error,
    submitAcsLogin,
    checkSSO
  }
}
